<script setup>
defineOptions({ name: "LoteModulePage" });
import { defineAsyncComponent } from "vue";
import {
  ArchiveBoxIcon,
  ClockIcon,
  Square3Stack3DIcon,
  ArrowsRightLeftIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  SparklesIcon,
  CubeIcon,
  BuildingOffice2Icon,
} from "@heroicons/vue/24/outline";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import heroBg from "@/assets/hero-lote.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const portalUrl =
  import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

useSeo({
  title: "Saiba de onde veio. Saiba para onde foi. | Kali Sistemas",
  description:
    "Controle lotes, fabricação, validade e movimentações dos seus produtos direto no Kali Gestão, com rastreabilidade desde a entrada até a venda.",
  image: heroBg,
});

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// Capacidades reais do adicional Lote, descritas no material do produto.
const capacidades = [
  {
    icon: ArchiveBoxIcon,
    titulo: "Origem",
    texto: "Identifique o lote relacionado a cada produto.",
  },
  {
    icon: ClockIcon,
    titulo: "Validade",
    texto: "Acompanhe datas de fabricação e vencimento.",
  },
  {
    icon: Square3Stack3DIcon,
    titulo: "Quantidade",
    texto: "Saiba quanto ainda existe disponível em cada lote.",
  },
  {
    icon: ArrowsRightLeftIcon,
    titulo: "Destino",
    texto: "Identifique as operações em que determinado lote foi utilizado.",
  },
];

const steps = [
  {
    title: "Produto entra na operação",
    description: "A mercadoria é recebida ou cadastrada no sistema.",
  },
  {
    title: "Lote é identificado",
    description: "São registradas as informações de fabricação, validade e quantidade.",
  },
  {
    title: "Estoque fica organizado por lote",
    description: "As quantidades disponíveis passam a ser acompanhadas por lote.",
  },
  {
    title: "Venda é realizada",
    description: "O lote utilizado fica relacionado à movimentação.",
  },
  {
    title: "Histórico permanece disponível",
    description: "As informações podem ser consultadas quando for preciso.",
  },
];

// Segmentos em que validade e rastreabilidade fazem parte da rotina.
const segmentos = [
  {
    icon: ShoppingBagIcon,
    titulo: "Supermercados e mercados",
    texto: "Produtos alimentícios e perecíveis.",
  },
  {
    icon: BuildingStorefrontIcon,
    titulo: "Padarias e distribuidores de alimentos",
    texto: "Ingredientes, insumos e produtos industrializados.",
  },
  {
    icon: TruckIcon,
    titulo: "Agropecuárias",
    texto: "Rações, sementes, insumos e produtos relacionados ao setor.",
  },
  {
    icon: SparklesIcon,
    titulo: "Perfumarias e cosméticos",
    texto: "Produtos controlados por lote e vencimento.",
  },
  {
    icon: CubeIcon,
    titulo: "Distribuidoras",
    texto: "Movimentações em maior escala que exigem organização por lote.",
  },
  {
    icon: BuildingOffice2Icon,
    titulo: "Indústrias",
    texto: "Produção, controle de qualidade e rastreabilidade.",
  },
];

const faqItems = [
  {
    question: "Consigo saber quanto ainda tenho de determinado lote?",
    answer:
      "Sim. O objetivo do controle é permitir acompanhar as quantidades relacionadas aos lotes dos produtos.",
  },
  {
    question: "Posso acompanhar a validade?",
    answer:
      "Sim. Datas de fabricação e vencimento podem fazer parte do controle dos lotes.",
  },
  {
    question: "O lote acompanha a venda?",
    answer:
      "Nas operações compatíveis, o lote utilizado pode ser relacionado à movimentação do produto.",
  },
  {
    question: "Posso consultar movimentações de um lote?",
    answer:
      "Sim. O histórico permite acompanhar as movimentações relacionadas a cada lote.",
  },
  {
    question: "Como solicito a ativação do módulo?",
    answer:
      "Pelo botão \"Solicitar ativação\" desta página, que leva direto ao Portal do Cliente.",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white">
    <SegmentHero title="Saiba de onde veio. Saiba para onde foi."
      description="Controle lotes, fabricação, validade e movimentações dos seus produtos diretamente no Kali Gestão. Tenha mais rastreabilidade sobre o estoque e acompanhe cada lote desde sua entrada até a saída."
      primary-label="Solicitar ativação" :primary-href="portalUrl" :primary-opens-demo="false"
      secondary-label="Ver Kali Gestão" secondary-href="/solucao/kali-gestao" :background-image="heroBg"
      background-class="bg-slate-900 bg-cover bg-center" overlay-variant="left" />

    <!-- Visão geral -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Não é apenas saber quando vence. É saber toda a história do produto.
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Produtos controlados por lote carregam informações importantes
            durante toda a operação. O Kali Gestão permite relacionar cada
            lote ao produto, sua quantidade, datas e movimentações, criando
            um histórico que acompanha a mercadoria ao longo do processo.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="cap in capacidades" :key="cap.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="cap.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ cap.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ cap.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Como funciona -->
    <section class="py-16 bg-gray-100 scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Do recebimento à venda, o lote acompanha o produto
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Da entrada ao histórico, sem controles paralelos.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-5">
          <div v-for="(step, idx) in steps" :key="step.title" v-reveal class="p-7 bg-white rounded-3xl shadow-sm">
            <div
              class="inline-flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-kali-blue-600">
              {{ idx + 1 }}
            </div>
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>

      </div>
    </section>

    <!-- Segmentos -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Especialmente útil onde validade e rastreabilidade fazem parte da rotina
          </h2>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="s in segmentos" :key="s.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="s.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ s.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ s.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <FaqPanel title="Dúvidas sobre Lote" :items="faqItems" cta-label="Solicitar ativação" :cta-href="portalUrl"
      description="Respostas rápidas sobre o módulo Lote. Se preferir, fale com nosso time e te orientamos no melhor cenário." />

    <CTA :activation-href="portalUrl" />
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
